import { memo } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { verifyTemporaryAccessPin } from '@/services/ws-companies/src/main/resources/rest-api';
import { fromDateTimeType } from '@/js/libs/api-helpers';
import { TextField, useForm } from '@/js/libs/form';
import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef';
import { Button, Modal, useNotification } from '@/js/libs/ui/src/V2';
import { useLazyRequest } from '@/js/libs/use-request';

import { getErrorMessage } from 'src/hooks/useRequestErrorNotification.ts';
import { ErrorCode } from 'src/types.ts';
import { PIN_FIELD_NAME, PIN_LENGTH } from './constants.ts';
import { createSchema, type FormValues, type SubmitValues } from './schema.ts';
import type { VerifyPinModalProps } from './types.ts';

export const VerifyPinModal = memo(function VerifyPinModal({ state, companyId, companyName, onConfirmed }: VerifyPinModalProps) {
  const [t] = useTranslation(['admin', 'common']);

  const notify = useNotification();

  const form = useForm<FormValues, unknown, SubmitValues>({
    defaultValues: { [PIN_FIELD_NAME]: '' },
    resolver: zodResolver(createSchema(t)),
    mode: 'onChange',
  });
  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
  } = form;

  const verifyRequest = useLazyRequest({
    apiMethod: verifyTemporaryAccessPin,
    ignoreErrors: [ErrorCode.Forbidden],
    onFail: (e) => notify.error({ title: t('common:ERROR'), body: getErrorMessage(e.response, t) }),
  });

  const onSubmit = useCallbackRef(async (values: SubmitValues) => {
    const response = await verifyRequest.load({ companyId, form: { pin: values.pin } });
    const expiresAtMs = fromDateTimeType(response?.data?.expiresAt)?.valueOf() ?? null;
    onConfirmed(expiresAtMs);
  });

  return (
    <Modal state={state} title={t('admin:LOG_IN_TO_COMPANY', { companyName })}>
      <FormProvider {...form}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Modal.Body>
            <TextField
              control={control}
              name={PIN_FIELD_NAME}
              label={t('admin:ENTER_THE_CODE')}
              maxLength={PIN_LENGTH}
              inputMode='numeric'
              intOnly
              disabled={isSubmitting}
              required
              dataTestId='temporary-access-pin-input'
            />
          </Modal.Body>
          <Modal.Footer>
            <Button ghost onClick={state.close} disabled={isSubmitting} dataTestId='temporary-access-cancel'>
              {t('common:CANCEL')}
            </Button>
            <Button type='submit' primary isLoading={isSubmitting} disabled={isSubmitting} dataTestId='temporary-access-verify'>
              {t('common:LOG_IN')}
            </Button>
          </Modal.Footer>
        </form>
      </FormProvider>
    </Modal>
  );
});
